"use client";

import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { AlertCircleIcon, InfoIcon, TruckIcon } from "lucide-react";

import { saveDraftBodyworkData } from "@/app/lib/actions/cat/vehicle-bodywork-step";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useVehicleAssignmentStepError } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-error";
import type { BodyworkData } from "@/lib/schemas/vehicle-assignment";
import {
  mapDraftToBodyworkFormState,
  type NeedsBodyworkAnswer,
  type ShippedAnswer,
} from "@/lib/schemas/cat/vehicle-bodywork-save-draft";
import { useBodyTypesQuery } from "@/lib/queries/body-types";
import { useComboboxMultiFieldFilter } from "@/hooks/use-combobox-multifield-filter";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";
import { MULESOFT_BODY_SHOPS_MOCK_RESPONSE } from "./mocks/body-shops.mulesoft.mock";

type BodyShopItem = {
  kvps: string;
  label: string;
  accountNumber: string;
  siret: string;
  postalCode: string;
};

const BODY_SHOPS: BodyShopItem[] =
  MULESOFT_BODY_SHOPS_MOCK_RESPONSE.records.map((record) => ({
    kvps: record.NoKVPS,
    label: record.RaisonSociale.trim(),
    accountNumber: record.NoCompte,
    siret: record.Siret,
    postalCode: record.CodePostal ?? "",
  }));

type SaveDraftBodyworkParams = Parameters<typeof saveDraftBodyworkData>[0];

function RetailVehicleBodyStep({
  onCanProceedChange,
  onChangeBodyworkData,
  onBeforeNextChange,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
  onChangeBodyworkData?: (data: BodyworkData) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
}) {
  const baseId = useId();
  const { showStepError } = useVehicleAssignmentStepError();
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );

  const bodyworkFormSeed = useMemo(
    () => mapDraftToBodyworkFormState(currentDraft),
    [currentDraft],
  );

  const bodyTypesQuery = useBodyTypesQuery({ sort: { name: "asc" } });

  const [needsBodyworkAnswer, setNeedsBodyworkAnswer] =
    useState<NeedsBodyworkAnswer>(bodyworkFormSeed.needsBodyworkAnswer);
  const [bodyTypeId, setBodyTypeId] = useState<string>(
    bodyworkFormSeed.bodyTypeId,
  );
  const [bodyShopPostalCode, setBodyShopPostalCode] = useState<string>(
    bodyworkFormSeed.bodyShopPostalCode,
  );
  const [bodyShopKvps, setBodyShopKvps] = useState<string>(
    bodyworkFormSeed.bodyShopKvps,
  );
  const [bodyShopAccountNumber, setBodyShopAccountNumber] = useState<string>(
    bodyworkFormSeed.bodyShopAccountNumber,
  );
  const [bodyShopSiret, setBodyShopSiret] = useState<string>(
    bodyworkFormSeed.bodyShopSiret,
  );
  const [shippedAnswer, setShippedAnswer] = useState<ShippedAnswer>(
    bodyworkFormSeed.shippedAnswer,
  );

  useEffect(() => {
    setNeedsBodyworkAnswer(bodyworkFormSeed.needsBodyworkAnswer);
    setBodyTypeId(bodyworkFormSeed.bodyTypeId);
    setBodyShopPostalCode(bodyworkFormSeed.bodyShopPostalCode);
    setBodyShopKvps(bodyworkFormSeed.bodyShopKvps);
    setBodyShopAccountNumber(bodyworkFormSeed.bodyShopAccountNumber);
    setBodyShopSiret(bodyworkFormSeed.bodyShopSiret);
    setShippedAnswer(bodyworkFormSeed.shippedAnswer);
  }, [bodyworkFormSeed]);

  const activeBodyTypes = useMemo(() => {
    const list = bodyTypesQuery.data?.bodyTypes ?? [];
    return list.filter((b) => b.isActive);
  }, [bodyTypesQuery.data?.bodyTypes]);

  const bodyTypesSelectable =
    bodyTypesQuery.isSuccess &&
    !bodyTypesQuery.isError &&
    activeBodyTypes.length > 0;

  const showBodyworkFields = needsBodyworkAnswer === "yes";
  const showBlockingBodyTypeError =
    showBodyworkFields &&
    !bodyTypesQuery.isPending &&
    (bodyTypesQuery.isError || !bodyTypesSelectable);

  const selectedBodyShop = useMemo<BodyShopItem | null>(() => {
    const fromList = BODY_SHOPS.find((c) => c.kvps === bodyShopKvps);
    if (fromList) {
      return fromList;
    }

    if (!bodyShopKvps) {
      return null;
    }

    return {
      kvps: bodyShopKvps,
      label: bodyShopKvps,
      accountNumber: bodyShopAccountNumber,
      siret: bodyShopSiret,
      postalCode: bodyShopPostalCode,
    };
  }, [bodyShopAccountNumber, bodyShopKvps, bodyShopPostalCode, bodyShopSiret]);

  const bodyShopName = selectedBodyShop?.label ?? "";

  const canProceed = useMemo(() => {
    if (needsBodyworkAnswer === "" || needsBodyworkAnswer === "no") {
      if (needsBodyworkAnswer === "no") return true;
      return false;
    }
    if (needsBodyworkAnswer === "yes") {
      if (bodyTypesQuery.isPending) return false;
      if (!bodyTypesSelectable) return false;
      if (!bodyTypeId) return false;
      if (!bodyShopKvps) return false;
      if (!bodyShopAccountNumber) return false;
      if (!bodyShopSiret) return false;
      if (!bodyShopPostalCode) return false;
      if (shippedAnswer !== "yes" && shippedAnswer !== "no") return false;
      return true;
    }
    return false;
  }, [
    bodyShopAccountNumber,
    bodyShopKvps,
    bodyShopPostalCode,
    bodyShopSiret,
    bodyTypeId,
    bodyTypesQuery.isPending,
    bodyTypesSelectable,
    needsBodyworkAnswer,
    shippedAnswer,
  ]);

  const bodyworkData = useMemo<BodyworkData>(() => {
    const parsedBodyTypeId = bodyTypeId
      ? Number.parseInt(bodyTypeId, 10)
      : null;
    return {
      bodyTypeId:
        parsedBodyTypeId !== null && Number.isFinite(parsedBodyTypeId)
          ? parsedBodyTypeId
          : null,
      bodyShopKvps: bodyShopKvps || null,
      bodyShopAccountNumber: bodyShopAccountNumber || null,
      bodyShopSiret: bodyShopSiret || null,
      bodyShopName: bodyShopName || null,
      bodyShopPostalCode,
      needsBodywork:
        needsBodyworkAnswer === "yes"
          ? true
          : needsBodyworkAnswer === "no"
            ? false
            : null,
      shippedToBodyShop:
        shippedAnswer === "yes" ? true : shippedAnswer === "no" ? false : null,
    };
  }, [
    bodyShopAccountNumber,
    bodyShopKvps,
    bodyShopName,
    bodyShopPostalCode,
    bodyShopSiret,
    bodyTypeId,
    needsBodyworkAnswer,
    shippedAnswer,
  ]);

  const clearBodyworkDetails = useCallback(() => {
    setBodyTypeId("");
    setBodyShopKvps("");
    setBodyShopAccountNumber("");
    setBodyShopSiret("");
    setBodyShopPostalCode("");
    setShippedAnswer("");
  }, []);

  const handleBeforeNext = useCallback(async () => {
    if (currentDraft === null) {
      showStepError("Aucun brouillon associé à cette affectation.");
      return false;
    }

    const params: SaveDraftBodyworkParams =
      needsBodyworkAnswer === "no"
        ? { id: currentDraft.id, needsBodywork: false }
        : {
            id: currentDraft.id,
            needsBodywork: true,
            bodyTypeId: Number.parseInt(bodyTypeId, 10),
            bodyShopKvps,
            bodyShopAccountNumber,
            bodyShopSiret,
            bodyShopPostalCode,
            shippedToBodyShop: shippedAnswer === "yes",
          };

    const result = await saveDraftBodyworkData(params);

    if (!result.success) {
      showStepError(
        result.message ??
          "Une erreur est survenue lors de l'enregistrement du carrossage.",
      );
      return false;
    }

    if (!result.data) {
      showStepError(
        "Aucune donnée de carrossage n'a été retournée après l'enregistrement.",
      );
      return false;
    }

    setCurrentDraft(result.data);

    return true;
  }, [
    bodyShopAccountNumber,
    bodyShopKvps,
    bodyShopPostalCode,
    bodyShopSiret,
    bodyTypeId,
    currentDraft,
    needsBodyworkAnswer,
    setCurrentDraft,
    shippedAnswer,
    showStepError,
  ]);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  useEffect(() => {
    onChangeBodyworkData?.(bodyworkData);
  }, [bodyworkData, onChangeBodyworkData]);

  useEffect(() => {
    onBeforeNextChange?.(handleBeforeNext);
    return () => onBeforeNextChange?.(null);
  }, [handleBeforeNext, onBeforeNextChange]);

  const bodyShopFilter = useComboboxMultiFieldFilter<BodyShopItem>({
    textSelectors: [(i) => i.label, (i) => i.kvps],
    digitSelectors: [(i) => i.postalCode],
  });

  return (
    <Card>
      <CardHeader className="space-y-3 pb-2">
        <CardTitle className="flex items-center gap-2">
          <TruckIcon className="text-primary size-5" />
          Véhicule Carrossé
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 pt-2">
        <Alert>
          <InfoIcon />
          <AlertDescription>
            Cette étape concerne les véhicules utilitaires nécessitant un
            carrossage spécifique (benne, plateau, fourgon aménagé, etc.).
          </AlertDescription>
        </Alert>

        <FieldGroup className="gap-6">
          <Field>
            <FieldLabel>
              Ce véhicule nécessite-t-il un carrossage&nbsp;?
            </FieldLabel>
            <FieldContent>
              <RadioGroup
                className="flex flex-wrap gap-4"
                value={needsBodyworkAnswer}
                onValueChange={(v) => {
                  const next = v as NeedsBodyworkAnswer;
                  setNeedsBodyworkAnswer(next);
                  if (next === "no") {
                    clearBodyworkDetails();
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={`${baseId}-nw-yes`} value="yes" />
                  <Label htmlFor={`${baseId}-nw-yes`}>Oui</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={`${baseId}-nw-no`} value="no" />
                  <Label htmlFor={`${baseId}-nw-no`}>Non</Label>
                </div>
              </RadioGroup>
            </FieldContent>
          </Field>

          {showBodyworkFields ? (
            <>
              {bodyTypesQuery.isPending ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-9 w-full max-w-sm" />
                </div>
              ) : null}

              {showBlockingBodyTypeError ? (
                <Alert variant="destructive">
                  <AlertCircleIcon />
                  <AlertDescription>
                    Impossible de charger les types de carrosserie. Vérifiez
                    votre connexion ou réessayez plus tard. Si le problème
                    persiste, contactez le support.
                  </AlertDescription>
                </Alert>
              ) : null}

              {showBodyworkFields &&
              bodyTypesQuery.isSuccess &&
              bodyTypesSelectable ? (
                <Field>
                  <FieldLabel>Type de carrosserie</FieldLabel>
                  <FieldContent>
                    <Select
                      value={bodyTypeId || undefined}
                      onValueChange={setBodyTypeId}
                    >
                      <SelectTrigger className="w-full max-w-sm">
                        <SelectValue placeholder="Sélectionner…" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeBodyTypes.map((bt) => (
                          <SelectItem key={bt.id} value={String(bt.id)}>
                            {bt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldContent>
                </Field>
              ) : null}

              {showBodyworkFields && bodyTypesSelectable ? (
                <>
                  <div className="w-full max-w-sm">
                    <Field>
                      <FieldLabel>Carrossier</FieldLabel>
                      <FieldContent>
                        <Combobox
                          items={BODY_SHOPS}
                          itemToStringLabel={(c: BodyShopItem) =>
                            `${c.label} (${c.postalCode}) - ${c.kvps}`
                          }
                          filter={bodyShopFilter}
                          value={selectedBodyShop}
                          onValueChange={(next: BodyShopItem | null) => {
                            if (!next) {
                              setBodyShopKvps("");
                              setBodyShopAccountNumber("");
                              setBodyShopSiret("");
                              setBodyShopPostalCode("");
                              return;
                            }

                            setBodyShopKvps(next.kvps);
                            setBodyShopAccountNumber(next.accountNumber);
                            setBodyShopSiret(next.siret);
                            setBodyShopPostalCode(next.postalCode);
                          }}
                        >
                          <ComboboxInput
                            placeholder="Rechercher…"
                            showClear={bodyShopKvps !== ""}
                            aria-label="Rechercher un carrossier"
                          />
                          <ComboboxContent>
                            <ComboboxEmpty>
                              Aucun carrossier trouvé.
                            </ComboboxEmpty>
                            <ComboboxList>
                              {(c: BodyShopItem) => (
                                <ComboboxItem key={c.kvps} value={c}>
                                  {`${c.label} (${c.postalCode}) - ${c.kvps}`}
                                </ComboboxItem>
                              )}
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                        <FieldDescription>
                          Vous pouvez filtrer en tapant la raison sociale, le
                          code postal (ex. 75001) ou le code KVPS.
                        </FieldDescription>
                      </FieldContent>
                    </Field>
                  </div>

                  <Field>
                    <FieldLabel>
                      Véhicule expédié chez le carrossier&nbsp;?
                    </FieldLabel>
                    <FieldContent>
                      <RadioGroup
                        className="flex flex-wrap gap-4"
                        value={shippedAnswer}
                        onValueChange={(v) =>
                          setShippedAnswer(v as ShippedAnswer)
                        }
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id={`${baseId}-sh-yes`} value="yes" />
                          <Label htmlFor={`${baseId}-sh-yes`}>Oui</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id={`${baseId}-sh-no`} value="no" />
                          <Label htmlFor={`${baseId}-sh-no`}>Non</Label>
                        </div>
                      </RadioGroup>
                    </FieldContent>
                  </Field>
                </>
              ) : null}
            </>
          ) : null}
        </FieldGroup>
      </CardContent>
    </Card>
  );
}

export { RetailVehicleBodyStep };
